<script setup lang="ts">
import {computed, onMounted, reactive, ref} from "vue";
import {useChatStore} from "../../store/chat.js";
import EmptyState from "../../components/global/EmptyState.vue";

const store = useChatStore()
const is_loading = ref(false)

const toDateString = (date: Date) => {
    const offset = date.getTimezoneOffset() * 60 * 1000

    return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

const statsParams = reactive({
    start_date: toDateString(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)),
    end_date: toDateString(new Date()),
})

const dateError = computed(() => {
    const {start_date, end_date} = statsParams

    if (!start_date || !end_date) {
        return 'Select both a start and end date.'
    }

    if (start_date > end_date) {
        return 'Start date cannot be after the end date.'
    }

    return ''
})

const stats = computed(() => store.stats)

const formatDuration = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) {
        return '—'
    }

    if (seconds < 60) {
        return `${seconds}s`
    }

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)

    if (hours) {
        return minutes ? `${hours}h ${minutes}m` : `${hours}h`
    }

    const remaining_seconds = seconds % 60

    return remaining_seconds ? `${minutes}m ${remaining_seconds}s` : `${minutes}m`
}

const getData = async () => {
    if (dateError.value) {
        return
    }

    is_loading.value = true
    try {
        await store.getStats({...statsParams})
    } finally {
        is_loading.value = false
    }
}

onMounted(async () => {
    await getData()
})
</script>

<template>
    <main class="chats-stats">
        <div class="chats-stats-filters">
            <brand-form-input label="Start date." variant="date" v-model="statsParams.start_date" />
            <brand-form-input label="End date." variant="date" v-model="statsParams.end_date" />
            <brand-button as="button" variant="primary" :disabled="!!dateError || is_loading" @click.prevent="getData">Apply</brand-button>
        </div>
        <p class="chats-stats-error" v-if="dateError">{{ dateError }}</p>

        <template v-if="stats">
            <div class="chats-stats-cards">
                <div class="chats-stats-card" :class="{'--is-overdue': stats.waiting_count > 0}">
                    <p>Waiting over {{ stats.overdue_minutes }} min</p>
                    <p>{{ stats.waiting_count }}</p>
                    <span>Open chats, right now</span>
                </div>
                <div class="chats-stats-card">
                    <p>Avg. first response</p>
                    <p>{{ formatDuration(stats.totals.average_first_response_time) }}</p>
                    <span>{{ stats.totals.responded_chat_count }} of {{ stats.totals.chat_count }} chats answered</span>
                </div>
                <div class="chats-stats-card">
                    <p>Avg. response</p>
                    <p>{{ formatDuration(stats.totals.average_response_time) }}</p>
                    <span>Across {{ stats.totals.response_count }} responses</span>
                </div>
            </div>

            <div class="chats-stats-table" v-if="stats.agents.length" :class="{'--is-loading': is_loading}">
                <div class="chats-stats-table-row --is-header">
                    <span>Agent</span>
                    <span>Chats</span>
                    <span>Responses</span>
                    <span>Avg. first response</span>
                    <span>Avg. response</span>
                    <span>Waiting &gt; {{ stats.overdue_minutes }} min</span>
                </div>
                <div class="chats-stats-table-row" v-for="agent in stats.agents" :key="agent.assignee_id ?? 'unassigned'">
                    <span>{{ agent.assignee?.name ?? 'Unassigned' }}</span>
                    <span>{{ agent.chat_count }}</span>
                    <span>{{ agent.response_count }}</span>
                    <span>{{ formatDuration(agent.average_first_response_time) }}</span>
                    <span>{{ formatDuration(agent.average_response_time) }}</span>
                    <span :class="{'--is-overdue': agent.waiting_count > 0}">{{ agent.waiting_count }}</span>
                </div>
            </div>

            <empty-state v-else icon="speechbubble-double" heading="No chats in this period." content="Response times show up here once agents reply to chats." />

            <p class="chats-stats-note">
                Averages cover chats created between {{ stats.start_date }} and {{ stats.end_date }}. Response times are only recorded from the response tracking release onward; auto replies are not counted.
            </p>
        </template>
    </main>
</template>

<style scoped lang="scss">
.chats-stats {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    overflow-y: auto;

    > * {
        flex-shrink: 0;
    }

    &-filters {
        display: flex;
        align-items: flex-end;
        gap: 2rem;
        flex-wrap: wrap;

        > * {
            flex: 1 1 24rem;
            width: auto;
        }

        > button {
            flex: 0 0 auto;
        }
    }

    &-error {
        @include font(1.4, 1.8, black);
        color: var(--color-red-raspberry);
    }

    &-cards {
        display: flex;
        gap: 2rem;
        flex-wrap: wrap;
    }

    &-card {
        flex: 1;
        min-width: 24rem;
        background: var(--color-white);
        border-radius: 4rem;
        padding: 2.4rem 4rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        border: .1rem solid transparent;

        p {
            @include font(1.4, 1.8, black);

            &:nth-child(2) {
                @include font(4.6, 4.6, book);
            }
        }

        span {
            @include font(1.2, 1.6, black);
            color: var(--color-hazy);
        }

        &.--is-overdue {
            background: rgba(196, 18, 64, .15);
            border-color: var(--color-red-raspberry);
        }

        @include dark() {
            background: var(--color-darth-maul);

            p {
                color: var(--color-mist);
            }

            &.--is-overdue {
                background: rgba(196, 18, 64, .35);
            }
        }
    }

    &-table {
        background: var(--color-white);
        border-radius: 2.4rem;
        padding: 1rem 2rem;
        overflow-x: auto;
        transition: opacity .3s ease-in-out;

        &.--is-loading {
            opacity: .5;
        }

        &-row {
            display: grid;
            grid-template-columns: 2fr repeat(5, 1fr);
            gap: 1rem;
            min-width: 72rem;
            padding: 1.4rem 0;
            border-bottom: .1rem solid var(--color-grey-mist);
            @include font(1.4, 1.8, book);

            &:last-child {
                border-bottom: none;
            }

            &.--is-header {
                @include font(1.2, 1.6, black);
                color: var(--color-hazy);
            }

            .--is-overdue {
                color: var(--color-red-raspberry);
                @include font(1.4, 1.8, black);
            }
        }

        @include dark() {
            background: var(--color-darth-maul);
            color: var(--color-mist);

            &-row {
                border-color: rgba(255, 255, 255, .1);
            }
        }
    }

    &-note {
        @include font(1.2, 1.6, book);
        color: var(--color-hazy);
    }
}
</style>
